import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaSave, FaArrowLeft, FaUpload, FaTrash } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import { sampleAPI } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #545b62;
  }
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
  flex: 1;
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
    font-size: 1.5rem;
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;

  &:hover {
    background-color: #1e7e34;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const FormCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
`;

const FormSection = styled.div`
  padding: 24px;
  border-bottom: 1px solid #dee2e6;

  &:last-child {
    border-bottom: none;
  }

  h2 {
    color: #495057;
    font-size: 1.25rem;
    margin-bottom: 20px;
    font-weight: 600;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  &.full-width {
    grid-column: 1 / -1;
  }

  label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 6px;
    font-size: 14px;
  }

  input, select, textarea {
    padding: 10px 12px;
    border: 2px solid #dee2e6;
    border-radius: 6px;
    font-size: 16px;
    transition: border-color 0.2s;

    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }

    &:invalid {
      border-color: #dc3545;
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  .error {
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
  }
`;

const DropzoneContainer = styled.div`
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover, &.active {
    border-color: #007bff;
    background-color: #f8f9ff;
  }

  &.error {
    border-color: #dc3545;
    background-color: #fff5f5;
  }

  .upload-icon {
    font-size: 48px;
    color: #6c757d;
    margin-bottom: 10px;
  }

  .upload-text {
    color: #6c757d;
    font-size: 14px;
    margin-bottom: 5px;
  }

  .upload-hint {
    color: #adb5bd;
    font-size: 12px;
  }
`;

const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  margin-top: 15px;
`;

const ImagePreview = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .remove-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;

    &:hover {
      background-color: #c82333;
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #6c757d;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const UploadOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const UploadModal = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  min-width: 400px;
  text-align: center;

  @media (max-width: 768px) {
    min-width: 90%;
    padding: 30px 20px;
  }
`;

const UploadTitle = styled.h2`
  color: #333;
  margin: 0 0 10px 0;
  font-size: 1.5rem;
`;

const UploadMessage = styled.p`
  color: #666;
  margin: 0 0 30px 0;
  font-size: 1rem;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 30px;
  background-color: #e9ecef;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 15px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const SampleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title_sample: '',
    description: '',
    type_of_fantasy: '',
    sample_use: 'Internal',
    yarn_type: 'Cotton',
    yarn_count: '',
    speed_mt_min: '',
    resolution_dpi: '',
    drop_size: '',
    altezza_testina_da_substrato: '',
    pretreatment: 'No',
    concentrazione_imbibente: '',
    steaming_time: '',
    sample_meters: '',
    dyed_sample_weight: '',
    dyed_sample_absorption: '',
    dyed_sample_absorption_percent: '',
    washing: 'No',
    yarn_original_characteristics: '',
    oven: false,
    oven_time: '',
    oven_temperature: '',
    oven_temperature_unit: 'C',
    notes: ''
  });

  const [files, setFiles] = useState({
    graphic_software_files: [],
    sample_images: [],
    knit_sample_result_image: null
  });

  const [existingImages, setExistingImages] = useState({
    graphic_software_files: [],
    sample_images: [],
    knit_sample_result_image: null
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const fetchSample = useCallback(async () => {
    try {
      setLoading(true);
      const data = await sampleAPI.getSample(id);
      setFormData({
        title_sample: data.title_sample,
        description: data.description || '',
        type_of_fantasy: data.type_of_fantasy,
        sample_use: data.sample_use,
        yarn_type: data.yarn_type,
        yarn_count: data.yarn_count,
        speed_mt_min: data.speed_mt_min.toString(),
        resolution_dpi: data.resolution_dpi.toString(),
        drop_size: data.drop_size != null ? data.drop_size.toString() : '',
        altezza_testina_da_substrato: data.altezza_testina_da_substrato ? data.altezza_testina_da_substrato.toString() : '',
        pretreatment: data.pretreatment || 'No',
        concentrazione_imbibente: data.concentrazione_imbibente || '',
        steaming_time: data.steaming_time,
        sample_meters: data.sample_meters ? data.sample_meters.toString() : '',
        dyed_sample_weight: data.dyed_sample_weight ? data.dyed_sample_weight.toString() : '',
        dyed_sample_absorption: data.dyed_sample_absorption ? data.dyed_sample_absorption.toString() : '',
        dyed_sample_absorption_percent: data.dyed_sample_absorption_percent ? data.dyed_sample_absorption_percent.toString() : '',
        washing: data.washing || 'No',
        yarn_original_characteristics: data.yarn_original_characteristics || '',
        oven: Boolean(data.oven),
        oven_time: data.oven_time != null ? data.oven_time.toString() : '',
        oven_temperature: data.oven_temperature != null ? data.oven_temperature.toString() : '',
        oven_temperature_unit: data.oven_temperature_unit || 'C',
        notes: data.notes || ''
      });

      setExistingImages({
        graphic_software_files: data.graphic_software_files
          ? data.graphic_software_files.split(',')
          : (data.graphic_software_file ? [data.graphic_software_file] : []),
        sample_images: data.sample_images ? data.sample_images.split(',') : [],
        knit_sample_result_image: data.knit_sample_result_image
      });

      setError(null);
    } catch (err) {
      setError('Error loading sample');
      console.error('Error fetching sample:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      fetchSample();
    }
  }, [isEdit, fetchSample]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const submitData = new FormData();
      
      // Add form data (avoid sending empty optional numeric fields)
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        // For optional numeric fields, don't send if empty string
        if ((key === 'altezza_testina_da_substrato' || key === 'sample_meters' || 
             key === 'dyed_sample_weight' || key === 'dyed_sample_absorption' || 
             key === 'dyed_sample_absorption_percent' || key === 'drop_size' ||
             key === 'oven_time' || key === 'oven_temperature') && value === '') {
          return;
        }
        // Skip empty optional select
        if (key === 'yarn_original_characteristics' && value === '') {
          return;
        }
        // Don't send oven details when the checkbox is off
        if ((key === 'oven_time' || key === 'oven_temperature' || key === 'oven_temperature_unit') && !formData.oven) {
          return;
        }
        submitData.append(key, value);
      });

      // Add files
      files.graphic_software_files.forEach(file => {
        submitData.append('graphic_software_files', file);
      });

      if (files.knit_sample_result_image) {
        submitData.append('knit_sample_result_image', files.knit_sample_result_image);
      }

      files.sample_images.forEach(file => {
        submitData.append('sample_images', file);
      });

      // Progress callback
      const onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      };

      let result;
      if (isEdit) {
        result = await sampleAPI.updateSample(id, submitData, onUploadProgress);
      } else {
        result = await sampleAPI.createSample(submitData, onUploadProgress);
      }

      navigate(`/sample/${result.id}`);
    } catch (err) {
      setError(isEdit ? 'Error updating sample' : 'Error creating sample');
      console.error('Error saving sample:', err);
    } finally {
      setSaving(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Dropzone for the two Graphic Software Files: one TIFF/TIF and one vector file.
  const graphicDropzone = useDropzone({
    accept: {
      'image/tiff': ['.tiff', '.tif'],
      'application/postscript': ['.ai', '.eps'],
      'application/illustrator': ['.ai'],
      'image/svg+xml': ['.svg'],
      'application/octet-stream': ['.af']
    },
    maxFiles: 6,
    onDrop: (acceptedFiles) => {
      setFiles(prev => ({
        ...prev,
        graphic_software_files: [...prev.graphic_software_files, ...acceptedFiles].slice(0, 6)
      }));
    }
  });

  // Dropzone for sample images
  const sampleImagesDropzone = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    maxFiles: 5,
    onDrop: (acceptedFiles) => {
      setFiles(prev => ({
        ...prev,
        sample_images: [...prev.sample_images, ...acceptedFiles].slice(0, 5)
      }));
    }
  });

  // Dropzone for knit sample result
  const knitResultDropzone = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFiles(prev => ({
          ...prev,
          knit_sample_result_image: acceptedFiles[0]
        }));
      }
    }
  });

  const removeFile = (type, index = null) => {
    setFiles(prev => {
      if ((type === 'sample_images' || type === 'graphic_software_files') && index !== null) {
        const newFiles = [...prev[type]];
        newFiles.splice(index, 1);
        return { ...prev, [type]: newFiles };
      } else {
        return { ...prev, [type]: null };
      }
    });
  };

  const removeExistingImage = (type, index = null) => {
    setExistingImages(prev => {
      if ((type === 'sample_images' || type === 'graphic_software_files') && index !== null) {
        const newFiles = [...prev[type]];
        newFiles.splice(index, 1);
        return { ...prev, [type]: newFiles };
      } else {
        return { ...prev, [type]: null };
      }
    });
  };

  const renderImagePreview = (file, onRemove) => {
    const key = file.name || file;
    
    // If it's a string, it's an already-saved server file - use JPEG preview
    if (typeof file === 'string') {
      return (
        <ImagePreview key={key}>
          <img 
            src={sampleAPI.getImageUrl(file)} 
            alt="Preview" 
          />
          <button className="remove-btn" onClick={onRemove} type="button">
            <FaTrash />
          </button>
        </ImagePreview>
      );
    }
    
    // If it's a newly uploaded TIFF, show placeholder
    const isTiff = file.name && (file.name.toLowerCase().endsWith('.tif') || file.name.toLowerCase().endsWith('.tiff'));
    
    if (isTiff) {
      return (
        <ImagePreview key={key}>
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#e9ecef',
            color: '#495057',
            fontSize: '11px',
            padding: '8px',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '32px', marginBottom: '8px' }}>🖼️</span>
            <span style={{ fontWeight: '600', marginBottom: '4px', wordBreak: 'break-all' }}>
              {file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name}
            </span>
            <span style={{ fontSize: '10px', color: '#6c757d' }}>
              Preview will be available after saving
            </span>
          </div>
          <button className="remove-btn" onClick={onRemove} type="button">
            <FaTrash />
          </button>
        </ImagePreview>
      );
    }
    
    // For other formats (JPG, PNG, etc), show normal preview
    return (
      <ImagePreview key={key}>
        <img 
          src={URL.createObjectURL(file)} 
          alt="Preview" 
        />
        <button className="remove-btn" onClick={onRemove} type="button">
          <FaTrash />
        </button>
      </ImagePreview>
    );
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <div className="spinner"></div>
          Loading sample...
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(isEdit ? `/sample/${id}` : '/')}>
          <FaArrowLeft /> Back
        </BackButton>
        <Title>{isEdit ? 'Edit Sample' : 'New Sample'}</Title>
        <SaveButton 
          form="sample-form" 
          type="submit" 
          disabled={saving}
        >
          <FaSave /> {saving ? 'Saving...' : 'Save'}
        </SaveButton>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <form id="sample-form" onSubmit={handleSubmit}>
        <FormCard>
          <FormSection>
            <h2>General Information</h2>
            <FormGrid>
              <FormGroup>
                <label htmlFor="title_sample">Title Sample *</label>
                <input
                  type="text"
                  id="title_sample"
                  name="title_sample"
                  value={formData.title_sample}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="type_of_fantasy">Type of Fantasy *</label>
                <input
                  type="text"
                  id="type_of_fantasy"
                  name="type_of_fantasy"
                  value={formData.type_of_fantasy}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="sample_use">Sample Use *</label>
                <select
                  id="sample_use"
                  name="sample_use"
                  value={formData.sample_use}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Internal">Internal</option>
                  <option value="Clients">Clients</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label htmlFor="yarn_type">Yarn Type *</label>
                <select
                  id="yarn_type"
                  name="yarn_type"
                  value={formData.yarn_type}
                  onChange={handleInputChange}
                  required>
                  <option value="Cotton">Cotton</option>
                  <option value="Cotton-polyester-blend">Cotton and polyester blend</option>
                  <option value="Cotton-cashmere-blend">Cotton and cashmere blend</option>
                  <option value="Wool">Wool</option>
                  <option value="Wool-acrylic-blend">Wool and acrylic blend</option>
                  <option value="Wool-nylon-blend">Wool and nylon blend</option>
                  <option value="Wool-cashmere-blend">Wool and cashmere blend</option>
                  <option value="Polyester">Polyester</option>
                  <option value="Viscose">Viscose</option>
                  <option value="Nylon">Nylon</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label htmlFor="yarn_original_characteristics">Original Characteristics of the Wire</label>
                <select
                  id="yarn_original_characteristics"
                  name="yarn_original_characteristics"
                  value={formData.yarn_original_characteristics}
                  onChange={handleInputChange}
                >
                  <option value="">—</option>
                  <option value="Raw">Raw</option>
                  <option value="Bleached">Bleached</option>
                  <option value="Optical white">Optical white</option>
                </select>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h2>Technical Specifications</h2>
            <FormGrid>
              <FormGroup>
                <label htmlFor="yarn_count">Yarn Count *</label>
                <input
                  type="text"
                  id="yarn_count"
                  name="yarn_count"
                  value={formData.yarn_count}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="speed_mt_min">Speed (mt/min) *</label>
                <input
                  type="number"
                  id="speed_mt_min"
                  name="speed_mt_min"
                  value={formData.speed_mt_min}
                  onChange={handleInputChange}
                  required
                  min="0"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="resolution_dpi">Resolution (dpi) *</label>
                <input
                  type="number"
                  id="resolution_dpi"
                  name="resolution_dpi"
                  value={formData.resolution_dpi}
                  onChange={handleInputChange}
                  required
                  min="0"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="drop_size">Drop Size</label>
                <input
                  type="number"
                  id="drop_size"
                  name="drop_size"
                  value={formData.drop_size}
                  onChange={handleInputChange}
                  min="0"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="altezza_testina_da_substrato">Printhead Height from Substrate (cm)</label>
                <input
                  type="number"
                  id="altezza_testina_da_substrato"
                  name="altezza_testina_da_substrato"
                  value={formData.altezza_testina_da_substrato}
                  onChange={handleInputChange}
                  min="0"
                  max="3"
                  step="0.1"
                  placeholder="0-3 cm"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="steaming_time">Steaming Time</label>
                <input
                  type="text"
                  id="steaming_time"
                  name="steaming_time"
                  value={formData.steaming_time}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="sample_meters">Sample Meters</label>
                <input
                  type="number"
                  id="sample_meters"
                  name="sample_meters"
                  value={formData.sample_meters}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="dyed_sample_weight">Dyed Sample Weight</label>
                <input
                  type="number"
                  id="dyed_sample_weight"
                  name="dyed_sample_weight"
                  value={formData.dyed_sample_weight}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="dyed_sample_absorption">Dyed Sample Absorption (gr)</label>
                <input
                  type="number"
                  id="dyed_sample_absorption"
                  name="dyed_sample_absorption"
                  value={formData.dyed_sample_absorption}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="dyed_sample_absorption_percent">Dyed Sample Absorption (%)</label>
                <input
                  type="number"
                  id="dyed_sample_absorption_percent"
                  name="dyed_sample_absorption_percent"
                  value={formData.dyed_sample_absorption_percent}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="%"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="pretreatment">Pretreatment</label>
                <select
                  id="pretreatment"
                  name="pretreatment"
                  value={formData.pretreatment}
                  onChange={handleInputChange}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label htmlFor="washing">Washing</label>
                <select
                  id="washing"
                  name="washing"
                  value={formData.washing}
                  onChange={handleInputChange}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </FormGroup>
              
              <FormGroup>
                <label htmlFor="concentrazione_imbibente">Wetting Agent Concentration (%)</label>
                <input
                  type="text"
                  id="concentrazione_imbibente"
                  name="concentrazione_imbibente"
                  value={formData.concentrazione_imbibente}
                  onChange={handleInputChange}
                  placeholder="e.g. 15%"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="oven" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="oven"
                    name="oven"
                    checked={formData.oven}
                    onChange={handleInputChange}
                    style={{ width: 'auto' }}
                  />
                  Predyed in oven
                </label>
              </FormGroup>

              {formData.oven && (
                <>
                  <FormGroup>
                    <label htmlFor="oven_time">Oven Time (min)</label>
                    <input
                      type="number"
                      id="oven_time"
                      name="oven_time"
                      value={formData.oven_time}
                      onChange={handleInputChange}
                      min="0"
                      step="0.1"
                      placeholder="minutes"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="oven_temperature">Oven Temperature</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="number"
                        id="oven_temperature"
                        name="oven_temperature"
                        value={formData.oven_temperature}
                        onChange={handleInputChange}
                        step="0.1"
                        placeholder="temperature"
                        style={{ flex: 1 }}
                      />
                      <select
                        id="oven_temperature_unit"
                        name="oven_temperature_unit"
                        value={formData.oven_temperature_unit}
                        onChange={handleInputChange}
                        style={{ width: '70px' }}
                      >
                        <option value="C">°C</option>
                        <option value="F">°F</option>
                      </select>
                    </div>
                  </FormGroup>
                </>
              )}
            </FormGrid>
          </FormSection>

          <FormSection>
            <h2>Graphic Software Files (max 6)</h2>
            <DropzoneContainer {...graphicDropzone.getRootProps()} className={graphicDropzone.isDragActive ? 'active' : ''}>
              <input {...graphicDropzone.getInputProps()} />
              <FaUpload className="upload-icon" />
              <div className="upload-text">Drag an image here or click to select</div>
              <div className="upload-hint">Up to 6 files: max 3 TIFF/TIF and max 3 AI, EPS, AF or SVG files</div>
            </DropzoneContainer>

            <ImagePreviewContainer>
              {existingImages.graphic_software_files.map((file, index) =>
                renderImagePreview(file, () => removeExistingImage('graphic_software_files', index))
              )}
              {files.graphic_software_files.map((file, index) =>
                renderImagePreview(file, () => removeFile('graphic_software_files', index))
              )}
            </ImagePreviewContainer>
          </FormSection>

          <FormSection>
            <h2>Sample Images (max 5)</h2>
            <DropzoneContainer {...sampleImagesDropzone.getRootProps()} className={sampleImagesDropzone.isDragActive ? 'active' : ''}>
              <input {...sampleImagesDropzone.getInputProps()} />
              <FaUpload className="upload-icon" />
              <div className="upload-text">Drag images here or click to select</div>
              <div className="upload-hint">Up to 5 images - Supported formats: JPG, PNG, GIF, BMP, WebP</div>
            </DropzoneContainer>

            <ImagePreviewContainer>
              {existingImages.sample_images.map((image, index) => 
                renderImagePreview(image, () => removeExistingImage('sample_images', index))
              )}
              {files.sample_images.map((file, index) => 
                renderImagePreview(file, () => removeFile('sample_images', index))
              )}
            </ImagePreviewContainer>
          </FormSection>

          <FormSection>
            <h2>Knit Sample Result Image</h2>
            <DropzoneContainer {...knitResultDropzone.getRootProps()} className={knitResultDropzone.isDragActive ? 'active' : ''}>
              <input {...knitResultDropzone.getInputProps()} />
              <FaUpload className="upload-icon" />
              <div className="upload-text">Drag an image here or click to select</div>
              <div className="upload-hint">Supported formats: JPG, PNG, GIF, BMP, WebP</div>
            </DropzoneContainer>

            <ImagePreviewContainer>
              {existingImages.knit_sample_result_image && !files.knit_sample_result_image && 
                renderImagePreview(existingImages.knit_sample_result_image, () => removeExistingImage('knit_sample_result_image'))
              }
              {files.knit_sample_result_image && 
                renderImagePreview(files.knit_sample_result_image, () => removeFile('knit_sample_result_image'))
              }
            </ImagePreviewContainer>
          </FormSection>

          <FormSection>
            <h2>Notes</h2>
            <FormGroup className="full-width">
              <label htmlFor="notes">Additional notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
                placeholder="Enter any additional notes..."
              />
            </FormGroup>
          </FormSection>
        </FormCard>
      </form>

      {/* Upload overlay with progress bar */}
      {uploading && (
        <UploadOverlay>
          <UploadModal>
            <UploadTitle>Uploading...</UploadTitle>
            <UploadMessage>
              {uploadProgress < 100 
                ? "Please wait for the upload to complete before closing the page"
                : "Processing on server..."}
            </UploadMessage>
            <ProgressBarContainer>
              <ProgressBarFill style={{ width: `${uploadProgress}%` }}>
                {uploadProgress}%
              </ProgressBarFill>
            </ProgressBarContainer>
            <p style={{ color: '#999', fontSize: '14px', margin: 0 }}>
              Do not close this page during upload
            </p>
          </UploadModal>
        </UploadOverlay>
      )}
    </Container>
  );
};

export default SampleForm;
